import { Controller } from '@hotwired/stimulus'
import zoid from 'zoid9031'
import * as analytics from '../utils/analytics'
import impression from '../utils/impression'

export default class extends Controller {
  static get targets () {
    return ['link', 'journey', 'section']
  }

  // This code is necessary to support loading passport experiences using SDK v0.6
  initialize () {
    // Prevent reinitialization or don't initialize if we're not in an iframe
    if (window.ProvenancePassportComponent || window.self === window.top) {
      return
    }

    // Prevent creation of a zoid frame unless it's the legacy approach, SDK < v0.8
    if (window.xprops && window.xprops.version) {
      console.debug('[passport_controller] NOT initializing legacy zoid frame with tag', this.element.dataset.passportId || this.element.dataset.experienceSlug, 'url', window.location.href)
      return
    }

    window.ProvenancePassportComponent = zoid.create({
      tag: this.element.dataset.passportId || this.element.dataset.experienceSlug,
      url: window.location.href,
      dimensions: {
        width: '100%',
        height: '100%'
      },
      autoResize: {
        height: true,
        width: false
      }
    })

    console.debug('[passport_controller][legacy] Created zoid frame with tag:', this.element.dataset.passportId || this.element.dataset.experienceSlug, window.location.href)
  }

  connect () {
    this.initAnalytics()

    if (window.xprops && window.xprops.initModals) {
      // Get the union of the two modal sets to avoid duplicates
      const modals = Object.values({ ...this.mapModals(), ...this.pageModals() })

      console.debug('[passport-controller] Initializing modals', modals)
      // Send list of modals to iframe
      window.xprops.initModals(modals)
    }
  }

  pageModals () {
    const modals = {}

    this.linkTargets.forEach(link => {
      const tag =
        link.dataset.zoidtag
          ? link.dataset.zoidtag.toLowerCase() // product passport
          : link.dataset.slug.toLowerCase() // product proof point

      // If tag not present insert into the list of modals
      if (!modals[tag]) {
        const href =
          link.href || `${window.location.origin}${link.dataset.href}`
        const urlString = this.reformatHref(href)

        modals[tag] = {
          tag,
          url: urlString,
          journey: !!link.dataset.journey
        }
      }
    })

    return modals
  }

  mapModals () {
    const modals = {}

    const el = document.querySelector('[data-map-geojson]')
    if (!el) {
      console.debug('[passport_controller] no [data-map-geojson] found. Skipping map modals initialization.')
      return
    }
    const geojson = JSON.parse(el.dataset.mapGeojson)

    geojson.features.forEach(f => {
      const tag = f.properties.slug

      if (f.properties.modal_url) {
        const url = this.reformatHref(f.properties.modal_url)

        modals[tag] = { tag, url }
      }
    })

    return modals
  }

  // eslint-disable-next-line class-methods-use-this
  reformatHref (href) {
    const url = new URL(href.replace('/dialog', ''))
    return `${url.origin}${url.pathname}/modal${url.search}`
  }

  initAnalytics () {
    this.checkVisibility()
  }

  checkVisibility () {
    // set up scroll event listener
    this.sectionsViewed = []
    impression(this.sectionTargets, entries => {
      this.trackImpressions(entries)
    })
  }

  trackImpressions (entries) {
    const { hasBatchData, passportId } = this.element.dataset

    entries.forEach(entry => {
      const { label } = entry.target.dataset
      if (entry.isIntersecting && !this.sectionsViewed.includes(label)) {
        this.sectionsViewed.push(label)

        if (passportId) {
          analytics.track(`Passport ${label} viewed`, {
            passport: passportId,
            url: window.location.href,
            hasBatchData
          })
          analytics.track(`passport${label}_viewed`, {
            object: `passport${label}`,
            action: 'viewed',
            object_id: passportId,
            has_batch_data: hasBatchData
          }, { adapter: 'segment' })
        } else {
          analytics.track(`Experience ${label} viewed`, {
            experienceId: this.element.dataset.experienceId,
            url: window.location.href
          })
          analytics.track(`experience${label}_viewed`, {
            object: `experience${label}`,
            action: 'viewed',
            object_id: this.element.dataset.experienceId,
            has_batch_data: hasBatchData
          }, { adapter: 'segment' })
        }
      }
    })
  }

  click (event) {
    const slug = this.element.dataset.passportId || this.element.dataset.experienceSlug
    const { zoidtag } = event.currentTarget.dataset

    analytics.track('Proof Point Clicked', {
      name: zoidtag,
      label: event.currentTarget.dataset.label,
      passport: slug,
      url: window.location.href
    })
    analytics.track('proofpoint_clicked', {
      object: 'proofpoint',
      action: 'clicked',
      context: 'passport',
      zoid_tag: zoidtag,
      event_label: event.currentTarget.dataset.label,
      slug
    }, { adapter: 'segment' })
  }
}
